import { useEffect, useState, useMemo } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { formatApiError } from '@/lib/utils';
import { showConfirm } from '@/components/ui/GlobalConfirm';
import { showAlert } from '@/components/ui/GlobalAlert';
import {
  Plus, X, Loader2, Receipt, TrendingUp, Clock, CheckCircle2,
  ChevronDown, ChevronUp, CreditCard, Trash2, Download, CalendarDays, Check
} from 'lucide-react';

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/40 transition-all";

const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtF = (s) => { try { return new Date(s).toLocaleDateString('es-AR'); } catch { return s || '—'; } };

const ESTADO_COLOR = {
  cobrado:   'text-green-300 bg-green-500/10 border-green-500/20',
  pendiente: 'text-[#C9A44C] bg-[#C9A44C]/10 border-[#C9A44C]/20',
  parcial:   'text-blue-300 bg-blue-500/10 border-blue-500/20',
  vencido:   'text-red-300 bg-red-500/10 border-red-500/20',
};

const estadoBadge = (e) => ESTADO_COLOR[e] || ESTADO_COLOR.pendiente;

/* ─── Modal crear honorario ─────────────────────────────────── */
function ModalCrear({ onClose, onCreated }) {
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [f, setF] = useState({
    cliente_id: '', expediente_id: '',
    tipo: 'pactado', monto: '', descripcion: '',
    entrega_inicial: '0', cuotas_total: '0', monto_cuota: '0',
    viaticos: '0', divisa: 'ARS', tna: '0',
    fecha_primera_cuota: '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get('/api/clientes/').then((r) => setClientes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (f.cliente_id) {
      api.get('/api/expedientes/').then((r) => {
        setExpedientes(r.data.filter((e) => e.cliente_id === f.cliente_id));
      }).catch(() => {});
    } else {
      setExpedientes([]);
    }
    setF((p) => ({ ...p, expediente_id: '' }));
  }, [f.cliente_id]);

  // Auto-calcular monto total cuando se cargan cuotas (con interés compuesto)
  useEffect(() => {
    const n = parseInt(f.cuotas_total) || 0;
    const val = parseFloat(f.monto_cuota) || 0;
    const entrega = parseFloat(f.entrega_inicial) || 0;
    const viaticos = parseFloat(f.viaticos) || 0;
    const r = (parseFloat(f.tna) || 0) / 1200;
    if (n > 0 && val > 0) {
      const sumaCuotas = r > 0
        ? Array.from({ length: n }, (_, i) => val * Math.pow(1 + r, i + 1)).reduce((a, b) => a + b, 0)
        : n * val;
      setF((p) => ({ ...p, monto: (entrega + sumaCuotas + viaticos).toFixed(2) }));
    }
  }, [f.cuotas_total, f.monto_cuota, f.entrega_inicial, f.viaticos, f.tna]);

  // Preview de cuotas (con interés compuesto + fecha de inicio editable)
  const previewCuotas = useMemo(() => {
    const n = parseInt(f.cuotas_total) || 0;
    const val = parseFloat(f.monto_cuota) || 0;
    if (n <= 0 || val <= 0) return [];
    const r = (parseFloat(f.tna) || 0) / 1200;
    const base = f.fecha_primera_cuota ? new Date(f.fecha_primera_cuota + 'T12:00:00') : new Date();
    return Array.from({ length: n }, (_, i) => {
      const fecha = new Date(base);
      fecha.setMonth(fecha.getMonth() + i + 1);
      const monto = r > 0 ? Math.round(val * Math.pow(1 + r, i + 1)) : val;
      return {
        num: i + 1,
        monto,
        fecha: fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      };
    });
  }, [f.cuotas_total, f.monto_cuota, f.tna, f.fecha_primera_cuota]);

  const submit = async (ev) => {
    ev.preventDefault(); setErr(''); setSaving(true);
    try {
      const body = {
        ...f,
        monto: parseFloat(f.monto) || 0,
        entrega_inicial: parseFloat(f.entrega_inicial) || 0,
        cuotas_total: parseInt(f.cuotas_total) || 0,
        monto_cuota: parseFloat(f.monto_cuota) || 0,
        viaticos: parseFloat(f.viaticos) || 0,
        tna: parseFloat(f.tna) || 0,
        expediente_id: f.expediente_id || null,
        fecha_primera_cuota: f.fecha_primera_cuota || null,
      };
      await api.post('/api/facturacion/honorarios', body);
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al crear honorario'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg my-4 max-h-[92vh] overflow-y-auto bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Nuevo honorario</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <select className={IC} value={f.cliente_id} onChange={set('cliente_id')} required>
            <option value="" className="bg-[#141414]">Seleccionar cliente *</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id} className="bg-[#141414]">
                {c.apellido ? `${c.apellido.toUpperCase()}, ${c.nombre}` : c.nombre}
              </option>
            ))}
          </select>
          <select className={IC} value={f.expediente_id} onChange={set('expediente_id')}>
            <option value="" className="bg-[#141414]">Expediente (opcional)</option>
            {expedientes.map((e) => (
              <option key={e.id} value={e.id} className="bg-[#141414]">{e.caratula}</option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <select className={IC} value={f.tipo} onChange={set('tipo')}>
              {['pactado', 'porcentaje', 'hora', 'mixto'].map((t) => (
                <option key={t} value={t} className="bg-[#141414]">{t.charAt(0).toUpperCase() + t.slice(1)}</option>
              ))}
            </select>
            <select className={IC} value={f.divisa} onChange={set('divisa')}>
              {['ARS', 'USD', 'EUR'].map((d) => (
                <option key={d} value={d} className="bg-[#141414]">{d}</option>
              ))}
            </select>
          </div>
          <textarea className={IC + ' resize-none'} rows={2} placeholder="Descripción" value={f.descripcion} onChange={set('descripcion')} />

          <div className="border-t border-white/10 pt-3">
            <p className="text-[11px] text-[#888] uppercase tracking-wide mb-2">Financiamiento (opcional)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#888] block mb-1">Entrega inicial</label>
                <input className={IC} type="number" value={f.entrega_inicial} onChange={set('entrega_inicial')} min="0" step="0.01" />
              </div>
              <div>
                <label className="text-[10px] text-[#888] block mb-1">Viáticos</label>
                <input className={IC} type="number" value={f.viaticos} onChange={set('viaticos')} min="0" step="0.01" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div>
                <label className="text-[10px] text-[#888] block mb-1">N° cuotas</label>
                <input className={IC} type="number" value={f.cuotas_total} onChange={set('cuotas_total')} min="0" />
              </div>
              <div>
                <label className="text-[10px] text-[#888] block mb-1">Valor cuota</label>
                <input className={IC} type="number" value={f.monto_cuota} onChange={set('monto_cuota')} min="0" step="0.01" />
              </div>
              <div>
                <label className="text-[10px] text-[#888] block mb-1">TNA (%)</label>
                <input className={IC} type="number" value={f.tna} onChange={set('tna')} min="0" step="0.01" />
              </div>
            </div>
            {(parseInt(f.cuotas_total) || 0) > 0 && (
              <div className="mt-2">
                <label className="text-[10px] text-[#888] block mb-1">Inicio de pagos (1° cuota)</label>
                <input className={IC} type="date" value={f.fecha_primera_cuota} onChange={set('fecha_primera_cuota')} />
              </div>
            )}

            {/* Preview cuotas */}
            {previewCuotas.length > 0 && (
              <div className="mt-3 border border-[rgba(201,164,76,0.15)] rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-[#C9A44C]/5 border-b border-[rgba(201,164,76,0.12)]">
                  <CalendarDays className="w-3.5 h-3.5 text-[#C9A44C]" />
                  <span className="text-[10px] uppercase tracking-wide text-[#C9A44C]">
                    Plan de cuotas — {previewCuotas.length} cuota{previewCuotas.length !== 1 ? 's' : ''}
                    {(parseFloat(f.tna)||0) > 0 && <span className="ml-1 normal-case tracking-normal text-[#C9A44C]/60">· TNA {f.tna}%</span>}
                  </span>
                  <span className="ml-auto text-[10px] text-[#888]">
                    Cuotas: {fmt(previewCuotas.reduce((s,c)=>s+c.monto,0))}
                  </span>
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {(parseFloat(f.entrega_inicial)||0) > 0 && (
                    <div className="flex justify-between items-center px-3 py-1.5 border-b border-white/5 bg-[#C9A44C]/5">
                      <span className="text-[10px] text-[#C9A44C]">Entrega inicial</span>
                      <span className="text-[10px] font-medium text-[#C9A44C]">{fmt(parseFloat(f.entrega_inicial))}</span>
                    </div>
                  )}
                  {previewCuotas.map((cu) => (
                    <div key={cu.num} className="flex justify-between items-center px-3 py-1.5 border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[#888] w-14">Cuota {cu.num}</span>
                        <span className="text-[10px] text-[#666]">{cu.fecha}</span>
                      </div>
                      <span className="text-[10px] font-medium">{fmt(cu.monto)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Monto total — auto-calculado o manual */}
          <div className="border-t border-white/10 pt-3">
            <label className="text-[10px] text-[#888] uppercase tracking-wide block mb-1">
              Monto total *
              {previewCuotas.length > 0 && <span className="ml-2 text-[#C9A44C] normal-case tracking-normal">(calculado automáticamente)</span>}
            </label>
            <input className={IC} type="number" placeholder="Monto total" value={f.monto} onChange={set('monto')} required min="0" step="0.01" />
          </div>
          <button type="submit" disabled={saving}
            className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear honorario'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─── Detalle honorario expandido ─────────────────────────────── */
function DetalleHonorario({ hon, onCambioEstado, onEliminar }) {
  const [cuotas, setCuotas] = useState([]);
  const [loadCuotas, setLoadCuotas] = useState(false);
  const [pagando, setPagando] = useState(null); // cuota_id que se está pagando
  const [editId, setEditId] = useState(null);   // cuota_id con la fecha en edición
  const [editVal, setEditVal] = useState('');
  const [guardandoFecha, setGuardandoFecha] = useState(false);

  const cargarCuotas = async () => {
    setLoadCuotas(true);
    try {
      const r = await api.get(`/api/facturacion/honorarios/${hon.id}/cuotas`);
      setCuotas(r.data);
    } finally { setLoadCuotas(false); }
  };

  useEffect(() => {
    if (hon.cuotas_total > 0) cargarCuotas();
  }, [hon.id]); // eslint-disable-line

  const descargarPdf = async (params = '', label = 'comprobante') => {
    try {
      const r = await api.get(`/api/facturacion/honorarios/${hon.id}/pdf${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}_${hon.id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch { /* silencioso */ }
  };

  const pagarCuota = async (cuota_id) => {
    setPagando(cuota_id);
    try {
      await api.post(`/api/facturacion/cuotas/${cuota_id}/pagar`, { metodo_pago: 'efectivo' });
      cargarCuotas();
      onCambioEstado();
    } finally { setPagando(null); }
  };

  const abrirEditFecha = (cu) => { setEditId(cu.id); setEditVal(String(cu.fecha_vencimiento || '').slice(0, 10)); };
  const guardarFecha = async (cuota_id) => {
    if (!editVal) return;
    setGuardandoFecha(true);
    try {
      await api.patch(`/api/facturacion/cuotas/${cuota_id}/fecha`, { fecha_vencimiento: editVal });
      setEditId(null);
      cargarCuotas();
    } catch (e) {
      showAlert(formatApiError(e.response?.data?.detail, 'No se pudo cambiar la fecha.'));
    } finally { setGuardandoFecha(false); }
  };

  const saldo = (hon.monto || 0) - (hon.monto_pagado || 0);

  return (
    <div className="px-4 pb-4 border-t border-white/5 pt-3 space-y-3">
      {/* Resumen financiero */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        {[
          { label: 'Total', val: fmt(hon.monto), color: '' },
          { label: 'Pagado', val: fmt(hon.monto_pagado), color: 'text-green-300' },
          { label: 'Saldo', val: fmt(saldo), color: saldo > 0 ? 'text-red-300' : 'text-green-300' },
          { label: 'Viáticos', val: fmt(hon.viaticos), color: '' },
        ].map(({ label, val, color }) => (
          <div key={label} className="bg-white/[0.02] rounded-lg p-2 min-w-0">
            <div className={`text-xs font-medium truncate ${color}`}>{val}</div>
            <div className="text-[9px] text-[#888] uppercase">{label}</div>
          </div>
        ))}
      </div>

      {/* Descripción */}
      {hon.descripcion && (
        <p className="text-xs text-[#ccc] bg-white/[0.02] rounded-lg p-2">{hon.descripcion}</p>
      )}

      {/* Cuotas */}
      {hon.cuotas_total > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#C9A44C] mb-2">
            Cuotas ({hon.cuotas_pagadas || 0}/{hon.cuotas_total} pagadas)
          </p>
          {loadCuotas ? (
            <Loader2 className="w-4 h-4 animate-spin text-[#C9A44C]" />
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {cuotas.map((cu) => (
                <div key={cu.id} className={`flex items-center justify-between p-2 rounded-lg text-xs border ${
                  cu.estado === 'pagada' ? 'bg-green-500/5 border-green-500/15'
                  : cu.estado === 'vencida' ? 'bg-red-500/5 border-red-500/15'
                  : 'bg-white/[0.02] border-white/5'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[#888]">Cuota {cu.numero_cuota}</span>
                    {editId === cu.id ? (
                      <span className="flex items-center gap-1">
                        <input type="date" value={editVal} onChange={(e) => setEditVal(e.target.value)} autoFocus
                          className="bg-white/[0.05] border border-[#C9A44C]/40 rounded px-1.5 py-0.5 text-[11px] text-[#F8F6F0] focus:outline-none" />
                        <button onClick={() => guardarFecha(cu.id)} disabled={guardandoFecha || !editVal}
                          className="text-green-400 hover:text-green-300 disabled:opacity-40" title="Guardar">
                          {guardandoFecha ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => setEditId(null)} className="text-[#888] hover:text-red-400" title="Cancelar">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ) : (
                      <button type="button" onClick={() => cu.estado !== 'pagada' && abrirEditFecha(cu)} disabled={cu.estado === 'pagada'}
                        className={`inline-flex items-center gap-1 text-[#666] ${cu.estado !== 'pagada' ? 'hover:text-[#C9A44C] cursor-pointer' : 'cursor-default'}`}
                        title={cu.estado !== 'pagada' ? 'Cambiar fecha de pago' : 'Cuota pagada'}>
                        {cu.fecha_vencimiento ? fmtF(cu.fecha_vencimiento) : 'sin fecha'}
                        {cu.estado !== 'pagada' && <CalendarDays className="w-3 h-3 opacity-50" />}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span>{fmt(cu.monto)}</span>
                    {cu.estado !== 'pagada' && (
                      <button onClick={() => pagarCuota(cu.id)} disabled={pagando === cu.id}
                        className="text-[9px] px-2 py-0.5 rounded-full bg-[#C9A44C]/20 text-[#C9A44C] hover:bg-[#C9A44C]/30 transition-all flex items-center gap-1">
                        {pagando === cu.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Pagar'}
                      </button>
                    )}
                    {cu.estado === 'pagada' && <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Acciones */}
      <div className="flex gap-2 pt-1 flex-wrap">
        <button onClick={() => descargarPdf('', 'comprobante')}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[rgba(201,164,76,0.25)] text-[#C9A44C] rounded-lg hover:bg-[#C9A44C]/10 transition-all"
          title="Comprobante completo con todas las cuotas">
          <Download className="w-3.5 h-3.5" /> Comprobante
        </button>
        <button onClick={() => descargarPdf('?solo_pendientes=true', 'deuda')}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-red-500/25 text-red-400 rounded-lg hover:bg-red-500/10 transition-all"
          title="Estado de deuda: cuotas pendientes y vencidas">
          <Download className="w-3.5 h-3.5" /> Deuda
        </button>
        <button onClick={() => descargarPdf('?solo_pagadas=true', 'recibo')}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-green-500/25 text-green-400 rounded-lg hover:bg-green-500/10 transition-all"
          title="Recibo de cuotas ya pagadas">
          <Download className="w-3.5 h-3.5" /> Recibo pagado
        </button>
        {hon.estado !== 'cobrado' && (
          <button onClick={() => onCambioEstado(hon.id, 'cobrado')}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-green-500/25 text-green-400 rounded-lg hover:bg-green-500/10 transition-all">
            <CheckCircle2 className="w-3.5 h-3.5" /> Marcar cobrado
          </button>
        )}
        <button onClick={() => onEliminar(hon.id)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-red-500/25 text-red-400 rounded-lg hover:bg-red-500/10 transition-all ml-auto">
          <Trash2 className="w-3.5 h-3.5" /> Eliminar
        </button>
      </div>
    </div>
  );
}

/* ─── Página principal Facturación ─────────────────────────────── */
export default function Facturacion() {
  const [honorarios, setHonorarios] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState(null);
  const [showCrear, setShowCrear] = useState(false);
  const [filtro, setFiltro] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const [h, s] = await Promise.allSettled([
        api.get('/api/facturacion/honorarios'),
        api.get('/api/facturacion/estadisticas'),
      ]);
      if (h.status === 'fulfilled') setHonorarios(h.value.data);
      if (s.status === 'fulfilled') setStats(s.value.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const cambiarEstado = async (id, estado) => {
    await api.patch(`/api/facturacion/honorarios/${id}/estado`, { estado });
    cargar();
  };

  const eliminar = async (id) => {
    if (!(await showConfirm('¿Eliminar este honorario? Esta acción no se puede deshacer.', { titulo: 'Eliminar honorario' }))) return;
    await api.delete(`/api/facturacion/honorarios/${id}`);
    cargar();
    if (expandido === id) setExpandido(null);
  };

  const filtrados = honorarios.filter((h) => {
    if (!filtro) return true;
    const t = `${h.cliente_nombre || ''} ${h.tipo || ''} ${h.descripcion || ''} ${h.estado || ''}`.toLowerCase();
    return t.includes(filtro.toLowerCase());
  });

  return (
    <DashboardLayout title="Facturación">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          {[
            { icon: TrendingUp, label: 'Total facturado', val: fmt(stats.total_facturado), color: '' },
            { icon: CheckCircle2, label: 'Cobrado', val: fmt(stats.total_cobrado), color: 'text-green-400' },
            { icon: Clock, label: 'Pendiente', val: fmt(stats.total_pendiente), color: 'text-[#C9A44C]' },
            { icon: Receipt, label: 'Honorarios', val: stats.por_estado?.reduce((a, e) => a + Number(e.cantidad), 0) || 0, color: '' },
          ].map(({ icon: Icon, label, val, color }) => (
            <div key={label} className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4">
              <Icon className={`w-5 h-5 mb-2 ${color || 'text-[#C9A44C]'}`} />
              <div className={`text-xl font-light mb-0.5 ${color}`} style={{ fontFamily: 'Playfair Display, serif' }}>{val}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#888]">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtro y botón */}
      <div className="flex gap-3 mb-4">
        <input className="flex-1 bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] transition-all"
          placeholder="Filtrar por cliente, tipo o estado..." value={filtro} onChange={(e) => setFiltro(e.target.value)} />
        <button onClick={() => setShowCrear(true)}
          className="text-[#070707] font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
          <Plus className="w-4 h-4" /> Nuevo honorario
        </button>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div>
      ) : filtrados.length === 0 ? (
        <div className="text-center py-20 text-[#666]">
          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{honorarios.length === 0 ? 'Sin honorarios registrados' : 'Sin resultados'}</p>
        </div>
      ) : (
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden space-y-px">
          {filtrados.map((h) => (
            <div key={h.id} className={expandido === h.id ? 'bg-white/[0.02]' : ''}>
              {/* Fila principal */}
              <div className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-white/[0.02] transition-all cursor-pointer"
                onClick={() => setExpandido(expandido === h.id ? null : h.id)}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-[#C9A44C]/10 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4 text-[#C9A44C]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{h.cliente_nombre || 'Sin cliente'}</div>
                    <div className="text-[11px] text-[#888] truncate">
                      {h.tipo} {h.caratula && `· ${h.caratula}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <div className="text-sm">{fmt(h.monto)}</div>
                    {h.cuotas_total > 0 && (
                      <div className="text-[10px] text-[#888]">{h.cuotas_total} cuotas</div>
                    )}
                  </div>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full border hidden sm:inline ${estadoBadge(h.estado)}`}>
                    {h.estado}
                  </span>
                  {expandido === h.id ? <ChevronUp className="w-4 h-4 text-[#888]" /> : <ChevronDown className="w-4 h-4 text-[#888]" />}
                </div>
              </div>
              {/* Detalle expandido */}
              {expandido === h.id && (
                <DetalleHonorario
                  hon={h}
                  onCambioEstado={(id, estado) => { cambiarEstado(id || h.id, estado || 'cobrado'); }}
                  onEliminar={eliminar}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {showCrear && <ModalCrear onClose={() => setShowCrear(false)} onCreated={() => { setShowCrear(false); cargar(); }} />}
    </DashboardLayout>
  );
}
